import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Mail,
  Minus,
  Phone,
  Play,
  Plus,
  Search,
  Star,
} from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import RequestDemoDrawer from '../components/RequestDemoDrawer';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  // State for project section tabs and accordion
  const [activeTab, setActiveTab] = useState('owner');
  const [openAccordion, setOpenAccordion] = useState('manage-data');

  // State for demo request drawer
  const [showDemoDrawer, setShowDemoDrawer] = useState(false);

  // Dynamic content for each stakeholder type
  const stakeholderContent = {
    owner: {
      title:
        'Take control of your construction projects with visibility into every step of the process.',
      sections: [
        {
          id: 'manage-data',
          title: 'Manage Data Across Projects',
          content:
            'Centralize all project information, documents, and communications in one platform. Track progress, budgets, and timelines across your entire portfolio with real-time dashboards and reporting.',
        },
        {
          id: 'build-collaborative',
          title: 'Build Collaboratively',
          content:
            'Connect all stakeholders - architects, contractors, and consultants - on a unified platform. Streamline approvals, reduce miscommunication, and ensure everyone works from the latest information.',
        },
        {
          id: 'improve-grow',
          title: 'Improve As You Grow',
          content:
            'Leverage data insights to optimize future projects. Learn from past performance, identify cost-saving opportunities, and scale your development operations efficiently.',
        },
      ],
    },
    'main-contractor': {
      title: 'Streamline your operations and deliver projects on time and within budget.',
      sections: [
        {
          id: 'project-execution',
          title: 'Project Execution Excellence',
          content:
            'Coordinate all trades, manage schedules, and track progress in real-time. Ensure smooth project delivery with integrated task management and resource allocation tools.',
        },
        {
          id: 'quality-safety',
          title: 'Quality & Safety Management',
          content:
            'Implement comprehensive safety protocols and quality control measures. Document inspections, manage compliance, and reduce risks with digital checklists and reporting.',
        },
        {
          id: 'financial-control',
          title: 'Financial Control & Reporting',
          content:
            'Track costs, manage change orders, and maintain profitability. Generate accurate progress billing and provide transparent reporting to clients and stakeholders.',
        },
      ],
    },
    'sub-contractor': {
      title: 'Optimize your specialized work and maintain strong client relationships.',
      sections: [
        {
          id: 'work-coordination',
          title: 'Work Coordination',
          content:
            'Stay synchronized with main contractors and other trades. Access project schedules, receive updates, and coordinate your work to avoid conflicts and delays.',
        },
        {
          id: 'document-access',
          title: 'Document Access & Sharing',
          content:
            'Access latest drawings, specifications, and project documents instantly. Share progress photos, submit RFIs, and maintain clear communication with project teams.',
        },
        {
          id: 'mobile-productivity',
          title: 'Mobile Productivity',
          content:
            'Work efficiently from the field with mobile access to all tools. Update progress, log time, capture issues, and stay connected even without internet connectivity.',
        },
      ],
    },
    customers: {
      title: 'Empower your workforce with modern tools and streamlined processes.',
      sections: [
        {
          id: 'workforce-management',
          title: 'Workforce Management',
          content:
            'Manage crew schedules, track attendance, and optimize labor allocation. Ensure the right people are in the right place at the right time for maximum productivity.',
        },
        {
          id: 'skill-development',
          title: 'Skill Development & Training',
          content:
            'Provide training modules, track certifications, and develop workforce capabilities. Create career progression paths and maintain compliance with industry standards.',
        },
        {
          id: 'safety-compliance',
          title: 'Safety & Compliance',
          content:
            'Ensure worker safety with digital safety protocols, incident reporting, and compliance tracking. Reduce accidents and maintain a culture of safety on-site.',
        },
      ],
    },
  };

  const handleRequestDemo = () => {
    setShowDemoDrawer(true);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          #awards-container::-webkit-scrollbar {
            display: none;
          }
          .font-inter {
            font-family: 'Inter', sans-serif;
          }
        `}
      </style>
      {/* Top Header Strip */}
      <div className="bg-brand-primary text-white px-4 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center text-base font-normal leading-[1.75em]">
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6 mb-2 sm:mb-0">
            <div className="flex items-center space-x-2">
              <Phone size={16} />
              <span>+65 3158 5747</span>
            </div>
            <div className="flex items-center space-x-2">
              <Mail size={16} />
              <span>builderbel@gmail.com</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Mail size={16} />
            <span>USA (English)</span>
            <ChevronDown size={16} />
          </div>
        </div>
      </div>

      {/* Navigation Header */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 lg:py-5">
          <div className="flex items-center justify-between">
            <Logo size="md" />

            <div className="hidden lg:flex items-center space-x-12">
              <a
                href="#products"
                className="text-[#1F1F1F] hover:text-brand-primary font-normal text-base leading-[1.75em] transition-colors"
              >
                Products
              </a>
              <a
                href="#solution"
                className="text-[#1F1F1F] hover:text-brand-primary font-normal text-base leading-[1.75em] transition-colors"
              >
                Solution
              </a>
              <a
                href="#stories"
                className="text-[#1F1F1F] hover:text-brand-primary font-normal text-base leading-[1.75em] transition-colors"
              >
                Customer Stories
              </a>
              <a
                href="#resources"
                className="text-[#1F1F1F] hover:text-brand-primary font-normal text-base leading-[1.75em] transition-colors"
              >
                Resources
              </a>
            </div>

            <div className="flex items-center space-x-3 lg:space-x-5">
              <button className="p-2.5 lg:p-3 text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors">
                <Search size={18} className="lg:w-5 lg:h-5" />
              </button>
              <button
                onClick={handleRequestDemo}
                className="bg-brand-primary hover:bg-brand-primary-hover text-white px-4 py-2.5 lg:px-6 lg:py-3 rounded-md font-bold text-sm leading-[1.2102272851126534em] uppercase tracking-wide transition-colors"
              >
                REQUEST A DEMO
              </button>
              <button
                onClick={handleLogin}
                className="border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white px-4 py-2.5 lg:px-6 lg:py-3 rounded-md font-bold text-sm leading-[1.2102272851126534em] uppercase tracking-wide transition-colors"
              >
                LOGIN
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="mb-6 lg:mb-8">
                <span className="inline-block bg-brand-primary text-white px-4 py-2 lg:px-6 lg:py-2 text-sm font-normal leading-[1.75em] uppercase tracking-wide">
                  CONSTRUCTION MANAGEMENT SOFTWARE
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[70px] font-bold text-[#1F1F1F] mb-6 lg:mb-8 leading-[1.3142857142857143em] tracking-[-2%]">
                The <span className="text-brand-primary">Construction</span> Cost Management Report
              </h1>

              <p className="text-base lg:text-base font-normal text-[#6E7077] mb-8 lg:mb-10 leading-[1.75em]">
                See how Procore breaks down communication silos from precon to closeout so you can
                build with less risk and better profits. Easy-to-use, mobile project management
                software that improves efficiency.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
                <button
                  onClick={handleRequestDemo}
                  className="bg-brand-primary hover:bg-brand-primary-hover text-white px-6 py-3 lg:px-8 lg:py-4 rounded-md font-bold text-sm leading-[1.2102272851126534em] uppercase tracking-wide transition-colors"
                >
                  REQUEST A DEMO
                </button>
                <button
                  onClick={handleLogin}
                  className="border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white px-6 py-3 lg:px-8 lg:py-4 rounded-md font-bold text-sm leading-[1.2102272851126534em] uppercase tracking-wide transition-colors"
                >
                  LOGIN
                </button>
              </div>
            </div>

            <div className="relative order-1 lg:order-2">
              <div className="relative flex justify-center">
                <div className="absolute inset-0 bg-brand-primary rounded-full w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] lg:w-[450px] lg:h-[450px] mx-auto"></div>
                <div className="relative z-10 flex items-center justify-center">
                  <img
                    src="/images/hero-image.png"
                    alt="Modern Construction House"
                    className="w-full h-auto max-w-[350px] sm:max-w-[500px] lg:max-w-[600px] mx-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Brands Section */}
      <section className="py-12 lg:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-8 md:gap-16 opacity-30 grayscale hover:opacity-60 transition-opacity duration-300">
            <img
              src="/images/brand-1.png"
              alt="Safeguard"
              className="h-10 sm:h-12 md:h-15 object-contain filter brightness-75"
            />
            <img
              src="/images/brand-2.png"
              alt="Logotype"
              className="h-8 sm:h-10 md:h-13 object-contain filter brightness-75"
            />
            <img
              src="/images/brand-3.png"
              alt="Durages"
              className="h-6 sm:h-8 md:h-11 object-contain filter brightness-75"
            />
            <img
              src="/images/brand-4.png"
              alt="Turbologo"
              className="h-8 sm:h-10 md:h-13 object-contain filter brightness-75"
            />
          </div>
        </div>
      </section>

      {/* Project Section */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Title Section */}
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold text-[#1F1F1F] mb-4 lg:mb-6 leading-[1.3333333333333333em]">
              <span className="text-brand-primary">1M+</span> construction projects.{' '}
              <span className="text-brand-primary">125+</span> countries.
            </h2>
            <p className="text-base font-normal text-[#6E7077] max-w-4xl mx-auto leading-[1.75em] px-4">
              See how Procore breaks down communication silos from precon to closeout so you can
              build with less risk and better profits. Easy-to-use, mobile project management
              software that improves efficiency.
            </p>
          </div>

          {/* Stakeholder Tabs */}
          <div className="flex justify-center mb-12 lg:mb-16">
            <div className="relative flex flex-wrap justify-center bg-gray-50 rounded-lg p-2">
              {[
                { id: 'owner', label: 'Owner', avatar: '/images/owner-avatar.png' },
                {
                  id: 'main-contractor',
                  label: 'Main Contractor',
                  avatar: '/images/main-contractor-avatar.png',
                },
                {
                  id: 'sub-contractor',
                  label: 'Sub Contractor',
                  avatar: '/images/sub-contractor-avatar.png',
                },
                { id: 'customers', label: 'Labour', avatar: '/images/owner-avatar.png' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setOpenAccordion(
                      stakeholderContent[tab.id as keyof typeof stakeholderContent]?.sections[0]
                        ?.id || ''
                    );
                  }}
                  className={`relative flex items-center px-4 py-3 lg:px-6 lg:py-3 rounded-lg transition-all duration-200 mb-2 ${
                    activeTab === tab.id
                      ? 'bg-brand-primary text-white shadow-md'
                      : 'bg-transparent text-[#1F1F1F] hover:bg-gray-100'
                  }`}
                >
                  <img
                    src={tab.avatar}
                    alt={`${tab.label} Avatar`}
                    className="w-6 h-6 lg:w-8 lg:h-8 rounded-full mr-2 lg:mr-3"
                  />
                  <span className="font-bold text-sm lg:text-base leading-[1.5em]">
                    {tab.label}
                  </span>
                  {activeTab === tab.id && (
                    <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[8px] border-transparent border-t-brand-primary"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            {/* Left Side - Content */}
            <div className="order-2 lg:order-1">
              <h3 className="text-xl sm:text-2xl lg:text-[28px] font-normal text-[#1F1F1F] mb-6 lg:mb-8 leading-[1.4285714285714286em]">
                {stakeholderContent[activeTab as keyof typeof stakeholderContent]?.title}
              </h3>

              <div className="space-y-4 lg:space-y-6">
                {stakeholderContent[activeTab as keyof typeof stakeholderContent]?.sections.map(
                  section => (
                    <div key={section.id} className="border-b border-[#EBEBF0] pb-4 lg:pb-6">
                      <button
                        onClick={() =>
                          setOpenAccordion(openAccordion === section.id ? '' : section.id)
                        }
                        className="flex items-center justify-between w-full mb-3 lg:mb-4 text-left"
                      >
                        <h4 className="text-lg lg:text-[20px] font-bold text-[#1F1F1F] leading-[1.4em] pr-4">
                          {section.title}
                        </h4>
                        <div className="w-6 h-6 bg-[#1F1F1F] rounded flex items-center justify-center flex-shrink-0">
                          {openAccordion === section.id ? (
                            <Minus size={16} className="text-white" />
                          ) : (
                            <Plus size={16} className="text-white" />
                          )}
                        </div>
                      </button>
                      {openAccordion === section.id && (
                        <p className="text-base font-normal text-[#6E7077] leading-[1.75em] transition-all duration-300 ease-in-out">
                          {section.content}
                        </p>
                      )}
                    </div>
                  )
                )}
              </div>

              <button className="mt-6 lg:mt-8 bg-brand-primary text-white px-6 py-3 lg:px-8 lg:py-4 rounded-md font-bold text-sm leading-[1.2102272851126534em] uppercase tracking-wide hover:bg-brand-primary-hover transition-colors">
                Learn More
              </button>
            </div>

            {/* Right Side - Image */}
            <div className="bg-white p-4 lg:p-8 rounded-xl order-1 lg:order-2">
              <img
                src="/images/project-interface.png"
                alt="Project Management Interface"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Rest of the sections will be implemented in subsequent todos */}

      {/* Features Section - Recommended Products */}
      <section className="py-12 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold text-[#1F1F1F] mb-4 lg:mb-6 leading-[1.3333333333333333em]">
              Recommended <span className="text-brand-primary">Products</span> for Main Contractors
            </h2>
            <p className="text-base font-normal text-[#6E7077] max-w-4xl mx-auto leading-[1.75em] px-4">
              See how Procore breaks down communication silos from precon to closeout so you can
              build with less risk and better profits. Easy-to-use, mobile project management
              software that improves efficiency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Project Management */}
            <div className="bg-white p-6 lg:p-8 rounded-lg text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 lg:w-20 lg:h-20 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-4 lg:mb-5">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-white">
                  <rect x="2.33" y="4.67" width="23.33" height="18.67" rx="2" fill="currentColor" />
                  <rect x="7" y="11.67" width="4.67" height="2.33" fill="white" />
                  <rect x="7" y="16.33" width="4.67" height="2.33" fill="white" />
                  <rect x="14" y="11.67" width="7" height="7" fill="white" />
                </svg>
              </div>
              <h3 className="text-lg lg:text-[20px] font-bold text-[#1F1F1F] mb-3 lg:mb-4 leading-[1.4em]">
                Project Management
              </h3>
              <p className="text-base font-normal text-[#6E7077] mb-4 lg:mb-6 leading-[1.75em]">
                Easy-to-use, mobile project management software that improves efficiency by
                connecting site and office for real-time visibility.
              </p>
              <button className="flex items-center justify-center text-brand-primary font-bold text-sm leading-[1.2102272851126534em] uppercase tracking-wide hover:text-brand-primary-hover transition-colors mx-auto">
                Learn More
                <ArrowRight size={16} className="ml-2" />
              </button>
            </div>

            {/* Project Financials */}
            <div className="bg-white p-8 rounded-lg text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-20 h-20 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-5">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-white">
                  <circle cx="14" cy="14" r="10" fill="currentColor" />
                  <text
                    x="14"
                    y="18"
                    fontSize="12"
                    fill="white"
                    textAnchor="middle"
                    fontWeight="bold"
                  >
                    $
                  </text>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#1F1F1F] mb-4">Project Financials</h3>
              <p className="text-base text-[#6E7077] mb-6 leading-relaxed">
                Manage accurate budgets with reliable data drawn from the site. See in real time how
                your spending decisions affect the profit you'll have at closeout.
              </p>
              <button className="flex items-center justify-center text-brand-primary font-bold text-sm uppercase tracking-wide hover:text-brand-primary-hover transition-colors mx-auto">
                Learn More
                <ArrowRight size={16} className="ml-2" />
              </button>
            </div>

            {/* Quality & Safety */}
            <div className="bg-white p-8 rounded-lg text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-20 h-20 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-5">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-white">
                  <path
                    d="M14 2.33L23.33 6.3V13.3C23.33 19.6 19.6 25.3 14 26.83C8.4 25.3 4.67 19.6 4.67 13.3V6.3L14 2.33Z"
                    fill="currentColor"
                  />
                  <path
                    d="M14 22.17C17.5 21 20.33 17.2 20.33 13.3V8.3L14 5.83L7.67 8.3V13.3C7.67 17.2 10.5 21 14 22.17Z"
                    fill="white"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#1F1F1F] mb-4">Quality & Safety</h3>
              <p className="text-base text-[#6E7077] mb-6 leading-relaxed">
                Mitigate risks and make tough decisions using real-time data from the site. Manage
                site diaries, inspections and incidents all in one place.
              </p>
              <button className="flex items-center justify-center text-brand-primary font-bold text-sm uppercase tracking-wide hover:text-brand-primary-hover transition-colors mx-auto">
                Learn More
                <ArrowRight size={16} className="ml-2" />
              </button>
            </div>

            {/* Design Coordination */}
            <div className="bg-white p-8 rounded-lg text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-20 h-20 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-5">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-white">
                  <circle cx="9" cy="9" r="3" fill="currentColor" />
                  <circle cx="19" cy="9" r="3" fill="currentColor" />
                  <circle cx="14" cy="19" r="3" fill="currentColor" />
                  <path d="M11.5 11L16.5 11" stroke="white" strokeWidth="2" />
                  <path d="M12 15L16 15" stroke="white" strokeWidth="2" />
                  <path d="M9 12L14 16" stroke="white" strokeWidth="2" />
                  <path d="M19 12L14 16" stroke="white" strokeWidth="2" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#1F1F1F] mb-4">Design Coordination</h3>
              <p className="text-base text-[#6E7077] mb-6 leading-relaxed">
                Bridge the gap between VDC teams and the operations team to quickly log, assign and
                access coordination issues in one central location.
              </p>
              <button className="flex items-center justify-center text-brand-primary font-bold text-sm uppercase tracking-wide hover:text-brand-primary-hover transition-colors mx-auto">
                Learn More
                <ArrowRight size={16} className="ml-2" />
              </button>
            </div>

            {/* Invoice Management */}
            <div className="bg-white p-8 rounded-lg text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-20 h-20 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-5">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-white">
                  <rect x="4.67" y="2.33" width="18.67" height="23.33" rx="2" fill="currentColor" />
                  <rect x="8" y="7" width="12" height="2" fill="white" />
                  <rect x="8" y="12" width="8" height="2" fill="white" />
                  <rect x="8" y="16" width="10" height="2" fill="white" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#1F1F1F] mb-4">Invoice Management</h3>
              <p className="text-base text-[#6E7077] mb-6 leading-relaxed">
                Streamline billing across stakeholders so everyone has access to the latest
                information in one organised platform.
              </p>
              <button className="flex items-center justify-center text-brand-primary font-bold text-sm uppercase tracking-wide hover:text-brand-primary-hover transition-colors mx-auto">
                Learn More
                <ArrowRight size={16} className="ml-2" />
              </button>
            </div>

            {/* Tender Management */}
            <div className="bg-white p-8 rounded-lg text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-20 h-20 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-5">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-white">
                  <rect x="4.67" y="2.33" width="18.67" height="23.33" rx="2" fill="currentColor" />
                  <rect x="8" y="8" width="12" height="1.5" fill="white" />
                  <rect x="8" y="12" width="8" height="1.5" fill="white" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#1F1F1F] mb-4">Tender Management</h3>
              <p className="text-base text-[#6E7077] mb-6 leading-relaxed">
                Save time throughout the tendering process with features that let you distribute
                information, collect tenders and track coverage all in one place.
              </p>
              <button className="flex items-center justify-center text-brand-primary font-bold text-sm uppercase tracking-wide hover:text-brand-primary-hover transition-colors mx-auto">
                Learn More
                <ArrowRight size={16} className="ml-2" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-12 lg:py-20 bg-brand-primary rounded-lg mx-4 relative overflow-hidden">
        <div className="absolute right-0 top-0 w-[300px] h-[150px] lg:w-[540px] lg:h-[270px] bg-white opacity-20 rounded-full transform translate-x-1/2 -translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold text-white mb-4 lg:mb-5 leading-[1.3333333333333333em]">
                Tap into 300+ integrations. Curated for you.
              </h2>
              <p className="text-base lg:text-lg font-normal text-white leading-[1.4444444444444444em]">
                See how connecting the apps you love with the Procore platform makes it easy to
                seamlessly manage construction.
              </p>
            </div>

            <div className="flex flex-col items-center space-y-8 lg:space-y-16 order-1 lg:order-2">
              <div className="flex items-center space-x-6 lg:space-x-16">
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white bg-opacity-10 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 bg-white rounded"></div>
                </div>
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white bg-opacity-10 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 bg-white rounded"></div>
                </div>
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white bg-opacity-10 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 bg-white rounded"></div>
                </div>
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white bg-opacity-10 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 bg-white rounded"></div>
                </div>
              </div>

              <div className="flex items-center space-x-6 lg:space-x-16">
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white bg-opacity-10 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 bg-white rounded"></div>
                </div>
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white bg-opacity-10 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 bg-white rounded"></div>
                </div>
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white bg-opacity-10 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 bg-white rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Switch to us Section */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold text-[#1F1F1F] mb-4 lg:mb-6 leading-[1.3333333333333333em]">
              There are so many <span className="text-brand-primary">reasons</span> to switch to us.
            </h2>
            <p className="text-base font-normal text-[#6E7077] max-w-4xl mx-auto leading-[1.75em] px-4">
              See how Procore breaks down communication silos from precon to closeout so you can
              build with less risk and better profits. Easy-to-use, mobile project management
              software that improves efficiency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Platform Card */}
            <div className="flex flex-col">
              <div className="relative mb-4 group cursor-pointer">
                <img
                  src="/images/platform-video.png"
                  alt="Platform Overview"
                  className="w-full h-60 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-40 rounded-lg flex items-center justify-center transition-all duration-300">
                  <div className="w-15 h-15 bg-black bg-opacity-50 group-hover:bg-opacity-70 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                    <Play
                      size={28}
                      className="text-white ml-1 transition-all duration-300 group-hover:scale-110"
                    />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4">
                  <span className="bg-brand-primary text-white px-4 py-2 rounded text-sm font-bold uppercase">
                    Platform
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg lg:text-[20px] font-bold text-[#1F1F1F] mb-3 leading-[1.4em]">
                  "From precon to closeout in one system."
                </h3>
                <p className="text-base font-normal text-[#6E7077] leading-[1.75em]">
                  Collaborate from precon to closeout by connecting your entire tech stack.
                </p>
              </div>
            </div>

            {/* Safety Card */}
            <div className="flex flex-col">
              <div className="relative mb-4 group cursor-pointer">
                <img
                  src="/images/safety-video.png"
                  alt="Safety Features"
                  className="w-full h-60 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-40 rounded-lg flex items-center justify-center transition-all duration-300">
                  <div className="w-15 h-15 bg-black bg-opacity-50 group-hover:bg-opacity-70 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                    <Play
                      size={28}
                      className="text-white ml-1 transition-all duration-300 group-hover:scale-110"
                    />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4">
                  <span className="bg-brand-primary text-white px-4 py-2 rounded text-sm font-bold uppercase">
                    Safety
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg lg:text-[20px] font-bold text-[#1F1F1F] mb-3 leading-[1.4em]">
                  "Our safety culture impacts the future."
                </h3>
                <p className="text-base font-normal text-[#6E7077] leading-[1.75em]">
                  Make sure everyone goes home at the end of the day with a proactive
                </p>
              </div>
            </div>

            {/* Collaboration Card */}
            <div className="flex flex-col">
              <div className="relative mb-4 group cursor-pointer">
                <img
                  src="/images/collaboration-video.png"
                  alt="Collaboration Tools"
                  className="w-full h-60 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-40 rounded-lg flex items-center justify-center transition-all duration-300">
                  <div className="w-15 h-15 bg-black bg-opacity-50 group-hover:bg-opacity-70 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                    <Play
                      size={28}
                      className="text-white ml-1 transition-all duration-300 group-hover:scale-110"
                    />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4">
                  <span className="bg-brand-primary text-white px-4 py-2 rounded text-sm font-bold uppercase">
                    Collaboration
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg lg:text-[20px] font-bold text-[#1F1F1F] mb-3 leading-[1.4em]">
                  "From precon to closeout in one system."
                </h3>
                <p className="text-base font-normal text-[#6E7077] leading-[1.75em]">
                  See how owners, head contractors, and specialty contractors build better
                </p>
              </div>
            </div>

            {/* Digitisation Card */}
            <div className="flex flex-col">
              <div className="relative mb-4 group cursor-pointer">
                <img
                  src="/images/digitisation-video.png"
                  alt="Digital Solutions"
                  className="w-full h-60 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-40 rounded-lg flex items-center justify-center transition-all duration-300">
                  <div className="w-15 h-15 bg-black bg-opacity-50 group-hover:bg-opacity-70 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                    <Play
                      size={28}
                      className="text-white ml-1 transition-all duration-300 group-hover:scale-110"
                    />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4">
                  <span className="bg-brand-primary text-white px-4 py-2 rounded text-sm font-bold uppercase">
                    Digitisation
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg lg:text-[20px] font-bold text-[#1F1F1F] mb-3 leading-[1.4em]">
                  "From precon to closeout in one system."
                </h3>
                <p className="text-base font-normal text-[#6E7077] leading-[1.75em]">
                  Move from slow, manual processes to the future an automated, digital
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 lg:py-20 bg-[#F9F9F9] rounded-lg mx-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center">
            {/* Left - Rating Cards */}
            <div className="bg-brand-primary text-white p-8 lg:p-12 rounded-lg order-2 lg:order-1">
              <div className="mb-8 lg:mb-12">
                <h3 className="text-2xl sm:text-3xl lg:text-[36px] font-bold text-white mb-4 lg:mb-5 leading-[1.3333333333333333em]">
                  Happy customers
                </h3>
                <p className="text-base lg:text-lg font-normal text-white leading-[1.4444444444444444em]">
                  Feedback from these happy customers helps us in reaching the heights
                </p>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div className="grid grid-cols-2 gap-5">
                  {/* Software Advice */}
                  <div className="bg-white bg-opacity-10 p-5 rounded-lg text-center">
                    <div className="w-20 h-20 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-5">
                      <img
                        src="/images/software-advice-logo.png"
                        alt="Software Advice"
                        className="w-10 h-10"
                      />
                    </div>
                    <div className="flex justify-center mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} fill="white" className="text-white" />
                      ))}
                    </div>
                    <div className="text-lg font-medium text-white">2442</div>
                  </div>

                  {/* G2 Rating */}
                  <div className="bg-white bg-opacity-10 p-5 rounded-lg text-center">
                    <div className="w-20 h-20 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-5">
                      <img src="/images/g2-logo.svg" alt="G2" className="w-10 h-10" />
                    </div>
                    <div className="flex justify-center mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} fill="white" className="text-white" />
                      ))}
                    </div>
                    <div className="text-lg font-medium text-white">2442</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-5">
                  {/* Apple Store */}
                  <div className="bg-white bg-opacity-10 p-5 rounded-lg text-center">
                    <div className="w-20 h-20 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-5">
                      <img src="/images/apple-logo.svg" alt="Apple Store" className="w-10 h-10" />
                    </div>
                    <div className="flex justify-center mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} fill="white" className="text-white" />
                      ))}
                    </div>
                    <div className="text-lg font-medium text-white">2442</div>
                  </div>

                  {/* Google Play */}
                  <div className="bg-white bg-opacity-10 p-5 rounded-lg text-center">
                    <div className="w-20 h-20 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-5">
                      <img
                        src="/images/google-play-logo.svg"
                        alt="Google Play"
                        className="w-10 h-10"
                      />
                    </div>
                    <div className="flex justify-center mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} fill="white" className="text-white" />
                      ))}
                    </div>
                    <div className="text-lg font-medium text-white">2442</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Testimonial */}
            <div className="bg-white p-8 lg:p-12 rounded-lg order-1 lg:order-2">
              <div className="mb-6 lg:mb-8">
                <div className="text-4xl lg:text-6xl text-brand-primary opacity-30 mb-3 lg:mb-4">
                  "
                </div>
                <p className="text-base font-normal text-[#1F1F1F] mb-6 lg:mb-8 leading-[1.75em]">
                  We had an incredible experience working with Landify and were impressed they made
                  such a big difference in only three weeks. Our team is so grateful for the
                  wonderful improvements they made and their ability to get familiar with the
                  product concept so quickly. It acted as a catalyst to take our design to the next
                  level and get more eyes on our product.
                </p>
              </div>

              <div className="flex items-center space-x-3 lg:space-x-4 mb-6 lg:mb-8">
                <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gray-300 rounded-full"></div>
                <div>
                  <h4 className="text-lg lg:text-[20px] font-bold text-black leading-[1.4em]">
                    Jenny Wilson
                  </h4>
                  <p className="text-base font-normal text-[#6E7077] leading-[1.75em]">
                    Vice President
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex space-x-5">
                  <button className="p-3 hover:bg-gray-100 rounded-full transition-colors">
                    <ArrowLeft size={20} className="text-[#1F1F1F]" />
                  </button>
                  <button className="p-3 hover:bg-gray-100 rounded-full transition-colors">
                    <ArrowRight size={20} className="text-[#1F1F1F]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold text-[#1F1F1F] mb-4 lg:mb-6 leading-[1.3333333333333333em]">
              BuilderBel Named #1 Construction Management Portal
            </h2>
            <p className="text-base font-normal text-[#6E7077] max-w-4xl mx-auto leading-[1.75em] px-4">
              See how <strong>BuilderBel</strong> breaks down communication silos from{' '}
              <strong>BuilderBel</strong> to closeout so you can build with less risk and better
              profits. Easy-to-use, mobile project management software that improves efficiency.
            </p>
          </div>

          <div className="flex items-center justify-center gap-4">
            {/* Left Arrow */}
            <button
              onClick={() => {
                const container = document.getElementById('awards-container');
                if (container) {
                  container.scrollBy({ left: -300, behavior: 'smooth' });
                }
              }}
              className="flex items-center justify-center w-12 h-12 rounded-full bg-brand-light hover:bg-brand-primary hover:text-white transition-colors group flex-shrink-0"
            >
              <ArrowLeft size={20} className="text-brand-primary group-hover:text-white" />
            </button>

            {/* Awards Container */}
            <div
              id="awards-container"
              className="flex items-center gap-8 overflow-x-auto scroll-smooth max-w-4xl"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              <div className="text-center flex-shrink-0">
                <img
                  src="/images/award-1.png"
                  alt="Construction Executive Magazine Leader"
                  className="w-24 h-32 object-contain mx-auto mb-4"
                />
                <p className="text-sm text-[#1F1F1F] font-medium max-w-[180px] leading-tight">
                  Construction Executive Magazine Top Construction Technology Firms
                </p>
              </div>

              <div className="text-center flex-shrink-0">
                <img
                  src="/images/award-2.png"
                  alt="G2 Best Construction Management"
                  className="w-24 h-32 object-contain mx-auto mb-4"
                />
                <p className="text-sm text-[#1F1F1F] font-medium max-w-[180px] leading-tight">
                  G2 Best Construction Management Software
                </p>
              </div>

              <div className="text-center flex-shrink-0">
                <img
                  src="/images/award-3.png"
                  alt="Software Report Top 100"
                  className="w-24 h-32 object-contain mx-auto mb-4"
                />
                <p className="text-sm text-[#1F1F1F] font-medium max-w-[180px] leading-tight">
                  The Software Report Top 100 Software Companies of 2021
                </p>
              </div>

              {/* Additional Awards for Scrolling Demo */}
              <div className="text-center flex-shrink-0">
                <img
                  src="/images/award-1.png"
                  alt="Innovation Award 2022"
                  className="w-24 h-32 object-contain mx-auto mb-4"
                />
                <p className="text-sm text-[#1F1F1F] font-medium max-w-[180px] leading-tight">
                  Innovation Excellence Award 2022
                </p>
              </div>

              <div className="text-center flex-shrink-0">
                <img
                  src="/images/award-2.png"
                  alt="Customer Choice Award"
                  className="w-24 h-32 object-contain mx-auto mb-4"
                />
                <p className="text-sm text-[#1F1F1F] font-medium max-w-[180px] leading-tight">
                  Customer Choice Award for Construction Software
                </p>
              </div>

              <div className="text-center flex-shrink-0">
                <img
                  src="/images/award-3.png"
                  alt="Industry Leadership Award"
                  className="w-24 h-32 object-contain mx-auto mb-4"
                />
                <p className="text-sm text-[#1F1F1F] font-medium max-w-[180px] leading-tight">
                  Industry Leadership Award 2023
                </p>
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => {
                const container = document.getElementById('awards-container');
                if (container) {
                  container.scrollBy({ left: 300, behavior: 'smooth' });
                }
              }}
              className="flex items-center justify-center w-12 h-12 rounded-full bg-brand-primary hover:bg-brand-primary-hover transition-colors flex-shrink-0"
            >
              <ArrowRight size={20} className="text-white" />
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 lg:py-20 bg-brand-primary rounded-2xl mx-4">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold text-white mb-4 lg:mb-6 leading-[1.3333333333333333em]">
            See what Procore can do for you.
          </h2>
          <p className="text-base font-normal text-white mb-8 lg:mb-10 leading-[1.75em]">
            Our users reported 90%+ customer satisfaction in 2020. Schedule a personal walkthrough
            to see the impact our software can have on your business.
          </p>

          <div className="flex justify-center items-center gap-4 mb-8">
            {/* Google Play Badge */}
            <img
              src="/images/google-play-badge.png"
              alt="Get it on Google Play"
              className="h-14 cursor-pointer hover:scale-105 transition-transform"
            />

            {/* App Store Badge */}
            <img
              src="/images/app-store-badge.png"
              alt="Download on the App Store"
              className="h-14 cursor-pointer hover:scale-105 transition-transform"
            />
          </div>

          <button
            onClick={handleRequestDemo}
            className="bg-white text-[#1F1F1F] px-12 py-4 rounded-md font-bold text-sm uppercase tracking-wide hover:bg-gray-100 transition-colors shadow-lg"
          >
            REQUEST DEMO
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F9F9F9] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-16">
            {/* Logo Column */}
            <div className="col-span-2 md:col-span-1 lg:col-span-1">
              <Logo size="md" className="mb-6 lg:mb-8" />
            </div>

            {/* Products Column */}
            <div>
              <h4 className="text-lg font-bold text-[#1F1F1F] mb-4 lg:mb-6 leading-[1.4444444444444444em]">
                Products
              </h4>
              <ul className="space-y-2 lg:space-y-3 text-base font-normal text-[#6E7077] leading-[1.75em]">
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Platform Overview
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Analytics
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    App Marketplace
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    BIM
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Design Coordination
                  </a>
                </li>
              </ul>
            </div>

            {/* Solutions Column */}
            <div>
              <h4 className="text-lg font-bold text-[#1F1F1F] mb-4 lg:mb-6 leading-[1.4444444444444444em]">
                Solutions
              </h4>
              <ul className="space-y-2 lg:space-y-3 text-base font-normal text-[#6E7077] leading-[1.75em]">
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Main Contractors
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Owners
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Subcontractors
                  </a>
                </li>
              </ul>
            </div>

            {/* Events Column */}
            <div>
              <h4 className="text-lg font-bold text-[#1F1F1F] mb-4 lg:mb-6 leading-[1.4444444444444444em]">
                Events
              </h4>
              <ul className="space-y-2 lg:space-y-3 text-base font-normal text-[#6E7077] leading-[1.75em]">
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Groundbreak
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Webinars
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h4 className="text-lg font-bold text-[#1F1F1F] mb-4 lg:mb-6 leading-[1.4444444444444444em]">
                Resources
              </h4>
              <ul className="space-y-2 lg:space-y-3 text-base font-normal text-[#6E7077] leading-[1.75em]">
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Resource Center
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    eBooks
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Community
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Job Industry News
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Support
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="text-lg font-bold text-[#1F1F1F] mb-4 lg:mb-6 leading-[1.4444444444444444em]">
                Company
              </h4>
              <ul className="space-y-2 lg:space-y-3 text-base font-normal text-[#6E7077] leading-[1.75em]">
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    About
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Investors
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#1F1F1F] transition-colors">
                    Legal
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-[#DEDEE4] mt-8 lg:mt-12 pt-6 lg:pt-8">
            <p className="text-sm font-normal text-[#6E7077] text-center leading-[1.7142857142857142em]">
              © 2025 BuilderBel Technologies, Inc
            </p>
          </div>
        </div>
      </footer>

      {/* Demo Request Drawer */}
      <RequestDemoDrawer isOpen={showDemoDrawer} onClose={() => setShowDemoDrawer(false)} />
    </div>
  );
};

export default LandingPage;
